
import { Coordinates } from './types';
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/utils/logger";

const logger = createLogger('location:profile');

/**
 * Check if the current user is authenticated
 */
export const checkUserAuthentication = async () => {
  try {
    logger.debug('Checking user authentication status');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      logger.debug('No active session found');
      return { isAuthenticated: false, profile: null };
    }
    
    // Get the user profile data
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, latitude, longitude, location_updated_at')
      .eq('id', session.user.id)
      .single();
    
    const isAuthenticated = !!profile;
    logger.debug(`User authentication check: ${isAuthenticated ? 'authenticated' : 'not authenticated'}`);
    return { isAuthenticated, profile };
  } catch (error) {
    logger.warn('Error checking user authentication:', error);
    return { isAuthenticated: false, profile: null };
  }
};

/**
 * Sync location with user profile in database
 */
export const syncLocationWithProfile = async (coords: Coordinates): Promise<boolean> => {
  try {
    logger.debug(`Syncing location to profile: (${coords.lat}, ${coords.lng})`);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      logger.debug('No session, skipping profile sync');
      return false;
    }
    
    // Update profile in database with current coordinates
    const { error } = await supabase
      .from('profiles')
      .update({
        latitude: coords.lat,
        longitude: coords.lng,
        location_updated_at: new Date().toISOString()
      })
      .eq('id', session.user.id);
    
    if (error) {
      logger.warn('Error updating profile location:', error);
      return false;
    }
    
    logger.debug('Location synced with profile successfully');
    return true;
  } catch (error) {
    logger.warn('Failed to sync location with profile:', error);
    return false;
  }
};

/**
 * Load location from user profile
 */
export const loadLocationFromProfile = async (
  staleTolerance: number, 
  saveLocation: (coords: Coordinates, syncBack: boolean) => void
): Promise<boolean> => {
  try {
    logger.debug('Attempting to load location from profile');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      logger.debug('No session, unable to load profile location');
      return false;
    }
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('latitude, longitude, location_updated_at')
      .eq('id', session.user.id)
      .single();
    
    if (!profile || !profile.latitude || !profile.longitude) {
      logger.debug('Profile has no location data');
      return false;
    }
    
    // Check if the profile location is stale
    if (profile.location_updated_at) {
      const locationDate = new Date(profile.location_updated_at);
      const now = new Date();
      const diffInMinutes = (now.getTime() - locationDate.getTime()) / (1000 * 60);
      
      if (diffInMinutes > staleTolerance) {
        logger.debug(`Profile location is stale (${diffInMinutes.toFixed(1)} minutes old), will request new location`);
        return false;
      }
      
      logger.debug(`Profile location is still fresh (${diffInMinutes.toFixed(1)} minutes old)`);
    } else {
      logger.debug('Profile has location but no timestamp');
    }
    
    // Use the profile location
    const coords = {
      lat: profile.latitude,
      lng: profile.longitude
    };
    
    logger.debug(`Using location from profile: (${coords.lat}, ${coords.lng})`);
    saveLocation(coords, false); // Save without syncing back to avoid loops
    return true;
  } catch (error) {
    logger.warn('Failed to load location from profile:', error);
    return false;
  }
};
